import { lazy, Suspense } from "react";
import {
  data,
  Links,
  Meta,
  Outlet,
  redirect,
  Scripts,
  ScrollRestoration,
  useLocation,
  useMatches,
} from "react-router";
import { getLocaleFromAcceptLanguage } from "~/i18n.server";
import type { Route } from "./+types/root";
import { GtmHeadScript, GtmNoScript } from "~/components/GoogleTagManager";
import { EmbeddedCartScript } from "~/components/EmbeddedCartScript";
import { LocalizationEntryRedirect } from "~/components/LocalizationEntryRedirect";
import { Toaster } from "~/components/ui/sonner";
import { TooltipProvider } from "~/components/ui/tooltip";
import { ObdCompatibilityCheckerProvider } from "~/components/products/pdp/ObdCompatibilityCheckerContext";
import { ObdCompatibilityCheckerDrawer } from "~/components/products/pdp/ObdCompatibilityCheckerDrawer";
import { DEV_MODE_ENABLED } from "~/lib/dev-mode";
import { DEFAULT_LOCALE, localeFromPathname } from "~/lib/locales";
import { assignExperiments } from "~/lib/experiments/assign.server";
import { AnalyticsRouteTracker } from "~/lib/analytics/AnalyticsRouteTracker";
import {
  HIDE_CHROME_INLINE_SCRIPT,
  isHideChromePath,
  isHideChromeRequest,
  useHideChrome,
  useHideChromeForEffects,
  useHideChromeSync,
} from "~/lib/hide-chrome";
import { BreakpointIndicator } from "~/components/BreakpointIndicator";
import "~/app.css";

const GTM_ID = import.meta.env.VITE_GTM_ID as string | undefined;

// Lazy + gated so the panel, its virtual-module change data, and the
// __devSrc-tagging transform never reach a production bundle (BNC-5440).
const DevModePanel = DEV_MODE_ENABLED
  ? lazy(() => import("~/components/dev-mode/DevModePanel").then((m) => ({ default: m.DevModePanel })))
  : null;

const LOCALE_COOKIE = "locale-checked";
const COOKIE_MAX_AGE = 60 * 60 * 24 * 7; // 7 days

function hasLocaleCookie(cookieHeader: string): boolean {
  return cookieHeader.split(";").some((c) => c.trim() === `${LOCALE_COOKIE}=1`);
}

function localeCookieHeader(): string {
  return `${LOCALE_COOKIE}=1; Path=/; Max-Age=${COOKIE_MAX_AGE}; SameSite=Lax`;
}

export async function loader({ request }: Route.LoaderArgs) {
  const url = new URL(request.url);

  // A/B assignment happens once, here, before any of the locale exits — two of
  // them return with no headers at all, so appending after them would leave
  // returning visitors (the majority) unassigned. Everything below routes
  // through this one Headers object: an object literal cannot carry two
  // Set-Cookie values, and both the locale cookie and the ab-* cookies can be
  // emitted on the same response.
  const experiments = assignExperiments(request);
  const headers = new Headers();
  for (const cookie of experiments.setCookies) {
    headers.append("Set-Cookie", cookie);
  }
  const payload = {
    experiments: { assignments: experiments.assignments, forced: experiments.forced },
    // `?hidechrome=1` on an allowed path: render without site chrome on the server too.
    hideChrome: isHideChromeRequest(url),
  };

  // Emailed quote links must land on the quote, not the locale home page.
  if (url.pathname.startsWith("/quote/")) {
    return data(payload, { headers });
  }

  // Already on a locale-prefixed route — nothing to do
  if (localeFromPathname(url.pathname) !== DEFAULT_LOCALE) {
    return data(payload, { headers });
  }

  // Pages other apps embed (legal pages) are English-only and must never bounce a
  // first-time visitor to /fr-CA/ — the embedding app asked for this exact URL. The
  // hash trigger is invisible here, so this keys on the path, not the flag.
  if (isHideChromePath(url.pathname)) {
    return data(payload, { headers });
  }

  // Already seen — skip the check
  const cookieHeader = request.headers.get("cookie") ?? "";
  if (hasLocaleCookie(cookieHeader)) {
    return data(payload, { headers });
  }

  const preferredLocale = getLocaleFromAcceptLanguage(
    request.headers.get("accept-language") ?? ""
  );

  headers.append("Set-Cookie", localeCookieHeader());

  // Carry the query string across so a first visit to `/?ab=b` (or a UTM-tagged
  // link) keeps it on the localized URL.
  if (preferredLocale !== "en") {
    return redirect(`/${preferredLocale}/${url.search}`, { headers });
  }

  // English — mark checked so we don't re-run on every page load
  return data(payload, { headers });
}

// Cloudflare advanced mode (build/client/_worker.js) means public/_headers does
// not apply to SSR responses, so the cache policy has to come from app code.
// `private` is the load-bearing directive: a shared-cache HIT would serve one
// visitor's arm to everyone. `Vary: Cookie` is not honoured by Cloudflare, so it
// is not a substitute.
//
// A route-level `headers` export shadows this one — app/routes/nav-blog-preview.tsx
// has one (passing the BFF's `Cache-Control: public, max-age=300` through), which
// must never be copied onto a page that carries a variant. Set-Cookie values from
// this loader survive regardless; React Router merges them in separately.
export function headers() {
  return { "Cache-Control": "private, no-cache, max-age=0, must-revalidate" };
}

export const links: Route.LinksFunction = () => [
  { rel: "icon", type: "image/svg+xml", href: "/favicon.svg" },
  { rel: "icon", type: "image/png", sizes: "180x180", href: "/favicon.png" },
  { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png" },
  // Images are plain no-CORS fetches, so the preconnect must omit `crossOrigin` to be reused.
  ...(import.meta.env.VITE_IMGIX_DOMAIN ? [{ rel: "preconnect", href: `https://${import.meta.env.VITE_IMGIX_DOMAIN}` }] : []),
];

export function Layout({ children }: { children: React.ReactNode }) {
  // Derived from the URL, like the i18n locale, so the document language always
  // matches the content (and updates on client-side navigation between locales).
  const lang = localeFromPathname(useLocation().pathname);
  const hideChrome = useHideChrome();
  const hideChromeEffects = useHideChromeForEffects();
  useHideChromeSync();
  // A page opts in with `handle = { pageTheme: "fleet" }` so macOS overscroll past the
  // page edges shows a dark color instead of white. Only the color, not the
  // .fleet scope, so portaled dialogs and toasts keep their light styling.
  const fleetPage = useMatches().some(
    (match) => (match.handle as { pageTheme?: string } | undefined)?.pageTheme === "fleet",
  );
  return (
    // suppressHydrationWarning: HIDE_CHROME_INLINE_SCRIPT sets data-hide-chrome on <html>
    // before hydration.
    <html lang={lang} className="light" suppressHydrationWarning>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1, viewport-fit=cover" />
        <Meta />
        <Links />
        {/* Before the GTM bootstrap: that reads the attribute this sets. */}
        <script dangerouslySetInnerHTML={{ __html: HIDE_CHROME_INLINE_SCRIPT }} />
        {GTM_ID && !hideChrome && (
          <>
            {/* Clarity loads from inside the container, so both hops are on the
                critical path — warm them together. Only when GTM is actually
                configured; a preconnect to a host we never call is pure cost. */}
            <link rel="preconnect" href="https://www.googletagmanager.com" />
            <link rel="preconnect" href="https://www.clarity.ms" />
            <GtmHeadScript gtmId={GTM_ID} />
          </>
        )}
      </head>
      <body className={fleetPage ? "bg-[#0D1118]" : undefined}>
        {/* First child of <body>, not <head> — an <iframe> in <head> ends head
            insertion mode and implicitly opens <body> early. */}
        {GTM_ID && !hideChrome && <GtmNoScript gtmId={GTM_ID} />}
        <TooltipProvider>
          <ObdCompatibilityCheckerProvider>
            <main className="max-w-full overflow-x-clip">{children}</main>
            {/* Mounted once for the whole site so the header, footer and PDP links can all open it. */}
            <ObdCompatibilityCheckerDrawer />
          </ObdCompatibilityCheckerProvider>
        </TooltipProvider>
        <Toaster />
        <ScrollRestoration />
        <Scripts />
        {!hideChromeEffects && (
          <>
            <EmbeddedCartScript />
            <LocalizationEntryRedirect />
            <AnalyticsRouteTracker />
            <BreakpointIndicator />
          </>
        )}
        {DevModePanel && !hideChromeEffects && (
          <Suspense fallback={null}>
            <DevModePanel />
          </Suspense>
        )}
      </body>
    </html>
  );
}

export default function Root() {
  return <Outlet />;
}
